import React, { useMemo, useState, useEffect } from 'react';
import { friendlyMessage } from "@/lib/errors";
import { showListSkeleton, useFirstLoad } from '@/lib/loadingState';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import DataTable, { Column } from '@/components/reusable/DataTable';
import { toast } from '@/hooks/use-toast';
import { formatWooPrice, woocommerceApi } from '@/lib/api/services/woocommerce';
import { Loader2, RefreshCw } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

interface WooCommerceOrdersProps {
    selectedAccountId: string | null;
    isConnected: boolean;
    currency?: string;
}

type WooOrder = {
    id: string;
    woocommerce_order_id: string;
    order_number?: string;
    status?: string;
    currency?: string;
    total?: string | number;
    woocommerce_created_at?: string;
};

const DEFAULT_LIMIT = 25;

export function WooCommerceOrders({ selectedAccountId, isConnected, currency = 'USD' }: WooCommerceOrdersProps) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [orders, setOrders] = useState<WooOrder[]>([]);
    const [loading, setLoading] = useState(false);
    const { hasLoaded, markLoaded } = useFirstLoad();
    const [syncing, setSyncing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // URL State helpers
    const getParam = (key: string, def: string) => searchParams.get(key) || def;
    const setParam = (key: string, value: string | null) => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            if (value === null) newParams.delete(key);
            else newParams.set(key, value);
            return newParams;
        });
    };

    // DISTINCT prefixed keys so pagination never collides with Commerce.tsx's
    // shared `page`/`limit` or with sibling Commerce tab components.
    const ordersPage = parseInt(getParam('wooOrdPage', '1'));
    const ordersPageSizeStr = getParam('wooOrdPageSize', '10');
    const ordersPageSize = parseInt(ordersPageSizeStr, 10);

    const loadOrders = async () => {
        if (!selectedAccountId) return;
        setLoading(true);
        try {
            const res = await woocommerceApi.getLocalOrders(selectedAccountId, DEFAULT_LIMIT, 0);
            setOrders(res.orders || []);
        } catch (err: any) {
            setError(friendlyMessage(err, 'Failed to load WooCommerce orders'));
        } finally {
            setLoading(false);
            markLoaded();
        }
    };

    useEffect(() => {
        if (isConnected && selectedAccountId) {
            loadOrders();
        }
    }, [isConnected, selectedAccountId]);

    const handleSyncOrders = async () => {
        if (!selectedAccountId) return;
        setSyncing(true);
        setError(null);
        try {
            const res = await woocommerceApi.syncOrders(selectedAccountId, 100, 'any');
            const count = res.synced || 0;
            toast(count > 0
                ? { title: 'Orders Synced', description: `Fetched ${count} ${count === 1 ? 'order' : 'orders'} from WooCommerce.` }
                : { title: 'Up to date', description: 'No new orders to sync from WooCommerce.' }
            );
            await loadOrders();
        } catch (err: any) {
            const msg = err?.message || 'Failed to sync WooCommerce orders';
            setError(msg);
            toast({
                title: 'Sync Failed',
                description: msg,
                variant: 'destructive',
            });
        } finally {
            setSyncing(false);
        }
    };

    const ordersColumns: Column<WooOrder>[] = useMemo(() => [
        {
            key: 'order',
            header: 'Order',
            render: (_, order) => (
                <span className="font-medium text-xs sm:text-sm whitespace-nowrap">
                    #{order.order_number || order.woocommerce_order_id}
                </span>
            ),
            className: 'text-xs sm:text-sm font-medium whitespace-nowrap',
        },
        {
            key: 'status',
            header: 'Status',
            render: (_, order) => (
                <Badge variant="outline" className="text-xs">
                    {order.status || 'unknown'}
                </Badge>
            ),
            className: 'text-xs sm:text-sm',
        },
        {
            key: 'total',
            header: 'Total',
            render: (_, order) => (
                <span className="text-xs sm:text-sm">
                    {formatWooPrice(Number(order.total || 0), currency || order.currency || 'USD')}
                </span>
            ),
            className: 'text-xs sm:text-sm',
        },
        {
            key: 'created',
            header: 'Created',
            render: (_, order) => (
                <span className="text-xs sm:text-sm text-muted-foreground whitespace-nowrap">
                    {order.woocommerce_created_at
                        ? new Date(order.woocommerce_created_at).toLocaleString()
                        : '—'}
                </span>
            ),
            className: 'text-xs sm:text-sm text-muted-foreground whitespace-nowrap',
        },
    ], [currency]);

    if (!isConnected) {
        return (
            <div className="text-center py-6 sm:py-8 space-y-3 sm:space-y-4">
                <p className="text-xs sm:text-sm text-muted-foreground">Please connect your WooCommerce store first to view orders.</p>
            </div>
        )
    }

    return (
        <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 sm:p-6 pb-2">
                <div>
                    <CardTitle>Orders</CardTitle>
                    <CardDescription>
                        Review WooCommerce orders synced into PhotonX Nexus.
                    </CardDescription>
                </div>
                <Button
                    size="sm"
                    variant="outline"
                    className="flex items-center gap-2"
                    onClick={handleSyncOrders}
                    disabled={syncing}
                >
                    {syncing ? (
                        <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Syncing...
                        </>
                    ) : (
                        <>
                            <RefreshCw className="h-4 w-4" />
                            Sync Orders
                        </>
                    )}
                </Button>
            </CardHeader>
            <CardContent className="p-0 sm:p-6 sm:pt-0">
                {error && <div className="text-xs sm:text-sm text-destructive p-3 bg-destructive/10 rounded mb-4 mx-4 sm:mx-0">{error}</div>}

                <div className="p-4 sm:p-0">
                    <DataTable
                        columns={ordersColumns}
                        data={orders}
                        currentPage={ordersPage}
                        pageSize={ordersPageSize}
                        onPageChange={(p) => setParam('wooOrdPage', p.toString())}
                        onPageSizeChange={(s) => {
                            setSearchParams(prev => {
                                const newParams = new URLSearchParams(prev);
                                newParams.set('wooOrdPageSize', s.toString());
                                newParams.set('wooOrdPage', '1');
                                return newParams;
                            });
                        }}
                        loading={showListSkeleton({ loading: loading || syncing, hasLoaded, willFetch: isConnected && !!selectedAccountId })}
                        emptyMessage="No orders synced yet. Click 'Sync Orders' to import from WooCommerce."
                        showPagination={true}
                    />
                </div>
            </CardContent>
        </Card>
    );
}
